import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import defaults from '@config/defaults'
import { PGA_COACH_PRIVATE_LISTING_IMAGE } from '@lib/pga-coach/constants'
import { getOfferingFormatIconKey } from '@lib/pga-coach/offering-format-icons'
import { composeOptimizedImageUrl } from '@utils/composeOptimizedImageUrl'
import { formatOfferingTypeLabel, formatMapPopupFacilityLine } from './utils'

/** Enough resolution for the 60px square crop; private thumbs share the listing photo. */
const MAP_POPUP_THUMB_WIDTH = 240

const isSvgImageUrl = (url) => {
  if (!url || typeof url !== 'string') return false
  const path = url.split('?')[0].split('#')[0]
  return path.toLowerCase().endsWith('.svg')
}

function buildOfferingThumbCandidates(offering) {
  const hit = offering?.rawHit || {}
  const fromRaw = hit.featured_image_url
    || hit.thumbnail_url
    || hit.hero_image_url
    || hit.image_url
    || hit.imageUrl
  const isPrivate = getOfferingFormatIconKey(offering?.format) === 'private'
  const sources = [
    offering?.imageUrl,
    fromRaw,
    isPrivate ? PGA_COACH_PRIVATE_LISTING_IMAGE : defaults.facilityImage,
    PGA_COACH_PRIVATE_LISTING_IMAGE,
  ].filter(Boolean)

  const optimizedOrdered = sources
    .map((src) => composeOptimizedImageUrl(src, MAP_POPUP_THUMB_WIDTH))
    .filter(Boolean)

  const seen = new Set()
  const out = optimizedOrdered.filter((url) => {
    if (seen.has(url)) return false
    seen.add(url)
    return true
  })
  return out.length
    ? out
    : [composeOptimizedImageUrl(PGA_COACH_PRIVATE_LISTING_IMAGE, MAP_POPUP_THUMB_WIDTH)]
}

const useStyles = makeStyles((theme) => ({
  infoWindowItem: {
    width: '100%',
    boxSizing: 'border-box',
    border: 0,
    background: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    padding: theme.spacing(1.5, 2),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(1.5, 2),
    },
    '& + &': {
      borderTop: '1px solid #e9edf3',
    },
  },
  infoWindowItemMobile: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
  infoWindowItemContent: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
  infoWindowThumbWrap: {
    width: 60,
    height: 60,
    borderRadius: 8,
    overflow: 'hidden',
    flexShrink: 0,
    backgroundColor: '#e5e7eb',
  },
  infoWindowThumb: (props) => ({
    width: '100%',
    height: '100%',
    objectFit: props.isSvg ? 'contain' : 'cover',
    objectPosition: 'center',
    display: 'block',
    border: 'none',
  }),
  infoWindowBody: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  infoWindowItemTitle: {
    fontSize: '0.95rem',
    lineHeight: 1.25,
    fontWeight: 700,
    color: '#111827',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  infoWindowCoachName: {
    marginTop: theme.spacing(0.25),
    color: '#4b5563',
    fontSize: '0.875rem',
    lineHeight: 1.35,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  infoWindowFacilityRow: {
    marginTop: theme.spacing(0.25),
    color: '#6b7280',
    fontSize: '0.875rem',
    lineHeight: 1.35,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  infoWindowPriceRow: {
    marginTop: theme.spacing(0.75),
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  infoWindowPrice: {
    fontWeight: 700,
    fontSize: '0.95rem',
    lineHeight: 1.2,
    color: '#111827',
  },
  formatPill: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 24,
    padding: theme.spacing(0, 1),
    borderRadius: 999,
    backgroundColor: theme.palette.blue.light25,
    color: '#8a94a3',
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 400,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  },
}))

function OfferingListItem({
  offering,
  facilityName,
  facilityKey,
  isMobileSheet,
  position,
  onSelectOffering,
  distanceLabel,
}) {
  const thumbCandidates = useMemo(() => buildOfferingThumbCandidates(offering), [
    offering.objectID,
    offering.format,
    offering.imageUrl,
    offering.rawHit,
  ])
  const [thumbAttempt, setThumbAttempt] = useState(0)
  const thumbSrc = thumbCandidates[Math.min(thumbAttempt, thumbCandidates.length - 1)]
  const isSvgThumb = isSvgImageUrl(thumbSrc)
  const classes = useStyles({ isSvg: isSvgThumb })
  const fallbackPriceInCents = Number(offering.priceInCents)
  const hasNumericPrice = !Number.isNaN(fallbackPriceInCents)
  const computedPriceLabel = offering.priceLabel || `$${Math.round((hasNumericPrice ? fallbackPriceInCents : 0) / 100)}`

  useEffect(() => {
    setThumbAttempt(0)
  }, [offering.objectID])

  const formatAccessibleLabel = formatOfferingTypeLabel(offering.format)

  return (
    <button
      type="button"
      className={`${classes.infoWindowItem} ${isMobileSheet ? classes.infoWindowItemMobile : ''}`.trim()}
      onClick={() => onSelectOffering?.(offering.objectID, {
        source: 'popup-item',
        facilityKey,
        position,
      })}
    >
      <div className={classes.infoWindowItemContent}>
        <div className={classes.infoWindowThumbWrap}>
          <img
            src={thumbSrc}
            alt={offering.title || 'Offering image'}
            className={classes.infoWindowThumb}
            onError={() => setThumbAttempt((i) => Math.min(i + 1, thumbCandidates.length - 1))}
          />
        </div>
        <div className={classes.infoWindowBody}>
          <Typography className={classes.infoWindowItemTitle}>{offering.title}</Typography>
          <Typography variant="body2" className={classes.infoWindowCoachName}>
            {offering.coach?.name || 'PGA Coach'}
          </Typography>
          <Typography variant="body2" className={classes.infoWindowFacilityRow}>
            {formatMapPopupFacilityLine({
              venueName: offering.venueName,
              facilityName,
              distanceLabel,
            })}
          </Typography>
          <div className={classes.infoWindowPriceRow}>
            <Typography className={classes.infoWindowPrice}>
              {computedPriceLabel}
            </Typography>
            <span className={classes.formatPill}>{formatAccessibleLabel}</span>
          </div>
        </div>
      </div>
    </button>
  )
}

OfferingListItem.propTypes = {
  offering: PropTypes.shape({
    objectID: PropTypes.string,
    title: PropTypes.string,
    venueName: PropTypes.string,
    priceLabel: PropTypes.string,
    priceInCents: PropTypes.number,
    format: PropTypes.string,
    imageUrl: PropTypes.string,
    rawHit: PropTypes.object,
    coach: PropTypes.shape({
      name: PropTypes.string,
      photoUrl: PropTypes.string,
    }),
  }).isRequired,
  facilityName: PropTypes.string,
  facilityKey: PropTypes.string.isRequired,
  isMobileSheet: PropTypes.bool,
  position: PropTypes.number,
  onSelectOffering: PropTypes.func,
  distanceLabel: PropTypes.string,
}

OfferingListItem.defaultProps = {
  facilityName: '',
  isMobileSheet: false,
  position: undefined,
  onSelectOffering: undefined,
  distanceLabel: '',
}

export default OfferingListItem
